import React, { useState, useEffect } from 'react';
import { customersAPI } from '@/lib/api';
import { formatCPFCNPJ } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SimpleModal } from '@/components/SimpleModal';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/components/Toast';
import { 
  Plus, 
  Users, 
  Edit, 
  Trash2, 
  Search, 
  Loader2,
  MapPin,
  Phone,
  Mail,
  Building2,
  MoreVertical
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export const CustomersPage = () => {
  const toast = useToast();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [buscandoCep, setBuscandoCep] = useState(false);
  const [buscandoCnpj, setBuscandoCnpj] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const emptyForm = {
    name: '',
    cpf_cnpj: '',
    email: '',
    phone: '',
    cep: '',
    logradouro: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    uf: '',
    ie: ''
  };
  
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => {
    loadCustomers();
  }, []);

  const loadCustomers = async () => {
    setLoading(true);
    try {
      const response = await customersAPI.getAll();
      setCustomers(response.data || []);
    } catch (error) {
      toast.error('Erro ao carregar clientes');
    } finally {
      setLoading(false);
    }
  };

  // Buscar endereço pelo CEP usando ViaCEP
  const buscarCep = async (cep) => {
    const cepLimpo = cep.replace(/\D/g, '');
    if (cepLimpo.length !== 8) return;
    
    setBuscandoCep(true);
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cepLimpo}/json/`);
      const data = await response.json();
      
      if (data.erro) {
        toast.error('CEP não encontrado');
        return;
      }
      
      setFormData(prev => ({
        ...prev,
        logradouro: data.logradouro || '',
        bairro: data.bairro || '',
        cidade: data.localidade || '',
        uf: data.uf || '',
        complemento: data.complemento || prev.complemento
      }));
      
      toast.success('Endereço encontrado!');
    } catch (error) {
      toast.error('Erro ao buscar CEP');
    } finally {
      setBuscandoCep(false);
    }
  };

  // Formatar CEP enquanto digita
  const handleCepChange = (e) => {
    let value = e.target.value.replace(/\D/g, '');
    if (value.length > 8) value = value.slice(0, 8);
    if (value.length > 5) {
      value = value.slice(0, 5) + '-' + value.slice(5);
    }
    setFormData({ ...formData, cep: value });

    // Buscar automaticamente quando CEP completo
    if (value.replace(/\D/g, '').length === 8) {
      buscarCep(value);
    }
  };

  // Buscar dados da empresa na Receita (BrasilAPI) pelo CNPJ
  const buscarCnpj = async (cnpj) => {
    const limpo = (cnpj || '').replace(/\D/g, '');
    if (limpo.length !== 14) {
      toast.error('Digite um CNPJ com 14 dígitos');
      return;
    }
    setBuscandoCnpj(true);
    try {
      const response = await fetch(`https://brasilapi.com.br/api/cnpj/v1/${limpo}`);
      if (!response.ok) {
        toast.error('CNPJ não encontrado na Receita');
        return;
      }
      const d = await response.json();
      const cepFmt = d.cep ? String(d.cep).replace(/\D/g, '').replace(/(\d{5})(\d{3})/, '$1-$2') : '';
      const tel = d.ddd_telefone_1 ? String(d.ddd_telefone_1).replace(/\D/g, '') : '';
      const telFmt = tel.length >= 10 ? tel.replace(/(\d{2})(\d{4,5})(\d{4})/, '($1) $2-$3') : tel;
      setFormData(prev => ({
        ...prev,
        name: prev.name || d.razao_social || d.nome_fantasia || '',
        cep: cepFmt || prev.cep,
        logradouro: [d.descricao_tipo_de_logradouro, d.logradouro].filter(Boolean).join(' ') || prev.logradouro,
        numero: d.numero || prev.numero,
        complemento: d.complemento || prev.complemento,
        bairro: d.bairro || prev.bairro,
        cidade: d.municipio || prev.cidade,
        uf: d.uf || prev.uf,
        email: prev.email || d.email || '',
        phone: prev.phone || telFmt || '',
      }));
      toast.success(`Dados de ${d.razao_social || 'empresa'} preenchidos!`);
    } catch (error) {
      toast.error('Erro ao consultar CNPJ');
    } finally {
      setBuscandoCnpj(false);
    }
  };

  // Formatar CPF/CNPJ e disparar busca automática ao completar CNPJ (14 dígitos)
  const handleCpfCnpjChange = (e) => {
    const value = e.target.value;
    setFormData({ ...formData, cpf_cnpj: value });
    if (value.replace(/\D/g, '').length === 14) {
      buscarCnpj(value);
    }
  };

  const openNewModal = () => {
    setFormData(emptyForm);
    setEditMode(false);
    setSelectedCustomer(null);
    setShowModal(true);
  };

  const openEditModal = (customer) => {
    setFormData({
      name: customer.name || '',
      cpf_cnpj: customer.cpf_cnpj || '',
      email: customer.email || '',
      phone: customer.phone || '',
      cep: customer.cep || '',
      logradouro: customer.logradouro || customer.address || '',
      numero: customer.numero || '',
      complemento: customer.complemento || '',
      bairro: customer.bairro || '',
      cidade: customer.cidade || '',
      uf: customer.uf || '',
      ie: customer.ie || ''
    });
    setEditMode(true);
    setSelectedCustomer(customer);
    setShowModal(true);
  };

  const openDeleteConfirm = (customer) => {
    setSelectedCustomer(customer);
    setShowDeleteConfirm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      // Montar endereço completo para compatibilidade
      const enderecoCompleto = [
        formData.logradouro,
        formData.numero ? `nº ${formData.numero}` : '',
        formData.complemento,
        formData.bairro,
        formData.cidade,
        formData.uf,
        formData.cep
      ].filter(Boolean).join(', ');

      const payload = {
        ...formData,
        address: enderecoCompleto
      };

      if (editMode && selectedCustomer) {
        await customersAPI.update(selectedCustomer.id, payload);
        toast.success('Cliente atualizado com sucesso!');
      } else {
        await customersAPI.create(payload);
        toast.success('Cliente cadastrado com sucesso!');
      }
      
      setShowModal(false);
      setFormData(emptyForm);
      setEditMode(false);
      setSelectedCustomer(null);
      loadCustomers();
    } catch (error) {
      toast.error(editMode ? 'Erro ao atualizar cliente' : 'Erro ao cadastrar cliente');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedCustomer) return;
    
    try {
      await customersAPI.delete(selectedCustomer.id);
      toast.success('Cliente excluído com sucesso!');
      setShowDeleteConfirm(false);
      setSelectedCustomer(null);
      loadCustomers();
    } catch (error) {
      toast.error('Erro ao excluir cliente');
    }
  };

  // Filtrar clientes
  const filteredCustomers = customers.filter(customer => {
    const search = searchTerm.toLowerCase();
    return (
      customer.name?.toLowerCase().includes(search) ||
      customer.cpf_cnpj?.includes(search) ||
      customer.email?.toLowerCase().includes(search) ||
      customer.phone?.includes(search)
    );
  });

  return (
    <div className="p-8 max-w-7xl mx-auto" data-testid="customers-page">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Clientes</h1>
          <p className="text-slate-600">Gerencie o cadastro de clientes</p>
        </div>
        <Button onClick={openNewModal} className="bg-slate-900 hover:bg-slate-800" data-testid="add-customer-button">
          <Plus className="w-4 h-4 mr-2" />
          Novo Cliente
        </Button>
      </div>

      {/* Barra de Busca */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
          <Input
            placeholder="Buscar por nome, CPF/CNPJ, email ou telefone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-customer-input"
          />
        </div>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 shadow-sm">
        {loading ? (
          <div className="p-8 text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-slate-400" />
            <p className="text-slate-500 mt-2">Carregando clientes...</p>
          </div>
        ) : filteredCustomers.length === 0 ? (
          <div className="p-8 text-center">
            <Users className="w-12 h-12 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">
              {searchTerm ? 'Nenhum cliente encontrado para esta busca' : 'Nenhum cliente cadastrado'}
            </p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>CPF/CNPJ</TableHead>
                <TableHead>Contato</TableHead>
                <TableHead>Endereço</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredCustomers.map(customer => (
                <TableRow key={customer.id} data-testid={`customer-row-${customer.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
                        <Users className="w-5 h-5 text-slate-400" />
                      </div>
                      <div>
                        <span className="font-semibold">{customer.name}</span>
                        {customer.ie && (
                          <p className="text-xs text-slate-500">IE: {customer.ie}</p>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="font-mono text-sm">{formatCPFCNPJ(customer.cpf_cnpj) || '-'}</TableCell>
                  <TableCell>
                    <div className="space-y-1 text-sm">
                      {customer.email && (
                        <div className="flex items-center gap-1 text-slate-600">
                          <Mail className="w-3 h-3" />
                          {customer.email}
                        </div>
                      )}
                      {customer.phone && (
                        <div className="flex items-center gap-1 text-slate-600">
                          <Phone className="w-3 h-3" />
                          {customer.phone}
                        </div>
                      )}
                      {!customer.email && !customer.phone && '-'}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm text-slate-600 max-w-xs truncate">
                      {customer.logradouro || customer.address ? (
                        <div className="flex items-start gap-1">
                          <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0" />
                          <span className="truncate">
                            {customer.logradouro || customer.address}
                            {customer.numero && `, ${customer.numero}`}
                            {customer.cidade && ` - ${customer.cidade}`}
                            {customer.uf && `/${customer.uf}`}
                          </span>
                        </div>
                      ) : '-'}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" data-testid={`customer-actions-${customer.id}`}>
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEditModal(customer)}>
                          <Edit className="w-4 h-4 mr-2" />
                          Editar
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          className="text-red-600"
                          onClick={() => openDeleteConfirm(customer)}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Excluir
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Modal de Cadastro/Edição */}
      <SimpleModal 
        open={showModal} 
        onClose={() => setShowModal(false)} 
        title={editMode ? "Editar Cliente" : "Novo Cliente"}
      >
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            {/* Dados Básicos */}
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label htmlFor="name">Nome / Razão Social *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="customer-name-input"
                />
              </div>
              <div>
                <Label htmlFor="cpf_cnpj">CPF/CNPJ</Label>
                <div className="flex gap-2">
                  <Input
                    id="cpf_cnpj"
                    value={formData.cpf_cnpj}
                    onChange={handleCpfCnpjChange}
                    placeholder="000.000.000-00"
                    data-testid="customer-cpf-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => buscarCnpj(formData.cpf_cnpj)}
                    disabled={buscandoCnpj || formData.cpf_cnpj.replace(/\D/g, '').length !== 14}
                    title="Buscar dados na Receita pelo CNPJ"
                    data-testid="customer-cnpj-lookup-btn"
                  >
                    {buscandoCnpj ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Receita'}
                  </Button>
                </div>
                <p className="text-[11px] text-slate-400 mt-1">Ao digitar um CNPJ completo, os dados da empresa são buscados automaticamente.</p>
              </div>
              <div>
                <Label htmlFor="ie">Inscrição Estadual</Label>
                <Input
                  id="ie"
                  value={formData.ie}
                  onChange={(e) => setFormData({ ...formData, ie: e.target.value })}
                  placeholder="Opcional"
                  data-testid="customer-ie-input"
                />
              </div>
            </div>
            
            {/* Contato */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  data-testid="customer-email-input"
                />
              </div>
              <div>
                <Label htmlFor="phone">Telefone</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="(00) 00000-0000"
                  data-testid="customer-phone-input"
                />
              </div>
            </div>

            {/* Endereço */}
            <div className="border-t pt-4 mt-4">
              <h3 className="font-medium text-sm text-slate-700 mb-3 flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                Endereço
              </h3>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="cep">CEP</Label>
                  <div className="relative">
                    <Input
                      id="cep"
                      value={formData.cep}
                      onChange={handleCepChange}
                      placeholder="00000-000"
                      maxLength={9}
                      data-testid="customer-cep-input"
                    />
                    {buscandoCep && (
                      <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 animate-spin text-slate-400" />
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Digite o CEP para buscar o endereço</p>
                </div>
                <div className="col-span-2">
                  <Label htmlFor="logradouro">Logradouro</Label>
                  <Input
                    id="logradouro"
                    value={formData.logradouro}
                    onChange={(e) => setFormData({ ...formData, logradouro: e.target.value })}
                    placeholder="Rua, Avenida, etc."
                    data-testid="customer-logradouro-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-4 gap-4 mt-3">
                <div>
                  <Label htmlFor="numero">Número</Label>
                  <Input
                    id="numero"
                    value={formData.numero}
                    onChange={(e) => setFormData({ ...formData, numero: e.target.value })}
                    placeholder="123"
                    data-testid="customer-numero-input"
                  />
                </div>
                <div>
                  <Label htmlFor="complemento">Complemento</Label>
                  <Input
                    id="complemento"
                    value={formData.complemento}
                    onChange={(e) => setFormData({ ...formData, complemento: e.target.value })}
                    placeholder="Apto, Sala..."
                    data-testid="customer-complemento-input"
                  />
                </div>
                <div className="col-span-2">
                  <Label htmlFor="bairro">Bairro</Label>
                  <Input
                    id="bairro"
                    value={formData.bairro}
                    onChange={(e) => setFormData({ ...formData, bairro: e.target.value })}
                    data-testid="customer-bairro-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 mt-3">
                <div className="col-span-2">
                  <Label htmlFor="cidade">Cidade</Label>
                  <Input
                    id="cidade"
                    value={formData.cidade}
                    onChange={(e) => setFormData({ ...formData, cidade: e.target.value })}
                    data-testid="customer-cidade-input"
                  />
                </div>
                <div>
                  <Label htmlFor="uf">UF</Label>
                  <Input
                    id="uf"
                    value={formData.uf}
                    onChange={(e) => setFormData({ ...formData, uf: e.target.value.toUpperCase() })}
                    maxLength={2}
                    placeholder="SP"
                    data-testid="customer-uf-input"
                  />
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex justify-end gap-2 mt-6">
            <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
              Cancelar
            </Button>
            <Button 
              type="submit" 
              className="bg-slate-900 hover:bg-slate-800" 
              disabled={saving}
              data-testid="save-customer-button"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Salvando...
                </>
              ) : (
                editMode ? 'Atualizar' : 'Cadastrar'
              )}
            </Button>
          </div>
        </form>
      </SimpleModal>

      {/* Dialog de Confirmação de Exclusão */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir o cliente <strong>{selectedCustomer?.name}</strong>?
              <br />
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
